import { push } from "connected-react-router";
import {
  change,
  setSubmitSucceeded,
  startSubmit,
  stopSubmit,
} from "redux-form";
import { all, call, fork, put, select, take } from "redux-saga/effects";
import { NotificationClient } from "../../config/view_const/notification";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import { Company } from "../../domain/model/company";
import { Project, Status } from "../../domain/model/project";
import { User } from "../../domain/model/user";
import { ICompanyRepository } from "../../domain/repository/company_repository";
import {
  IProjectRepository,
  TCreateProjectParams,
  TUpdateProjectParams,
} from "../../domain/repository/project_repository";
import {
  IFileUseCase,
  createFileUsecase,
} from "../../domain/usecase/file_usecase";
import { IApiClient, createApiClient } from "../../infra/api/client";
import { createCompanyAPI } from "../../infra/api/service/company_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import {
  IAppNotificationMessageActionCreator,
  createAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
  ClientProjectDetailActionType,
  IClientProjectDetailActionCreator,
  IRequestCloseProjectDetailAction,
  IRequestCreateProjectDetailAction,
  IRequestDraftProjectDetailAction,
  IRequestGetProjectDetailAction,
  IRequestOpenProjectDetailAction,
  IRequestUpdateProjectDetailAction,
  createClientProjectDetailActionCreator,
} from "../action/client_project_action";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import {
  CreateProjectDetailByClientResponse,
  GetProjectDetailByClientResponse,
  UpdateProjectDetailByClientResponse,
} from "../response/project_response";
import { AppState } from "../store/app_state";
import { AuthState } from "../store/auth_state";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: IClientProjectDetailActionCreator =
  createClientProjectDetailActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

function* handleGetProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestGetProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_GET_PROJECT
      );
      const project: Project = yield call(getProject, action.item.projectId);
      const company: Company = yield call(getCompany, project.companyID);
      const res: GetProjectDetailByClientResponse =
        new GetProjectDetailByClientResponse();
      res.project = project;
      res.company = company;
      yield put(actionCreator.callbackGetProjectDetailAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackGetProjectDetailAction(false));
    }
  }
}

function* handleCreateProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestCreateProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_CREATE_PROJECT
      );
      yield put(startSubmit("clientProjectMainCreateForm"));

      const createParams: TCreateProjectParams = {
        name: action.item.name,
        description: action.item.description,
        background: action.item.background,
        preferentialExperience: action.item.preferentialExperience,
        thumbnailURL: "",
        place: action.item.place,
        workingHourRange: action.item.workingHourRange,
        paymentRange: action.item.paymentRange,
        workingDuration: action.item.workingDuration,
        occupations: action.item.occupations,
        operatingDate: action.item.operatingDate,
        isHideCompany: action.item.isHideCompany,
        isOpen: false,
      };

      const project_create: Project = yield call(createProject, createParams);

      let thumbnailURL: string;
      const authSelector = (state: AppState) => state.authReducer;
      const _authState: AuthState = yield select(authSelector);
      const user: User | null = _authState.user;
      if (action.item.uploadFileThumbnailURL) {
        thumbnailURL = yield call(
          createProjectThumbnail,
          user,
          action.item.uploadFileThumbnailURL,
          project_create.id
        );
      } else if (/.*\/project\/default.*/.test(action.item.thumbnailURL)) {
        // project/default が含まれているならdefault 画像
        thumbnailURL = "";
      } else {
        thumbnailURL = action.item.thumbnailURL;
      }

      const updateParams: TUpdateProjectParams = {
        id: project_create.id,
        name: project_create.name,
        description: project_create.description,
        background: project_create.background,
        preferentialExperience: project_create.preferentialExperience,
        thumbnailURL,
        place: project_create.place,
        workingHourRange: project_create.workingHourRange,
        paymentRange: project_create.paymentRange,
        workingDuration: project_create.workingDuration,
        occupations: project_create.occupations,
        operatingDate: project_create.operatingDate,
        isHideCompany: project_create.isHideCompany,
      };

      const [project_update, company] = yield all([
        call(updateProject, updateParams),
        call(getCompany, project_create.companyID),
      ]);

      const status = action.item.status;
      const projectId = project_create.id;
      switch (Number(status)) {
        case Status.Status_Open:
          yield call(openProject, projectId);
          break;
        case Status.Status_Draft:
          yield call(draftProject, projectId);
          break;
        case Status.Status_Close:
          yield call(closeProject, projectId);
          break;
        case Status.Status_OnlyScout:
          yield call(onlyScoutProject, projectId);
          break;
      }

      const res: CreateProjectDetailByClientResponse =
        new CreateProjectDetailByClientResponse();
      res.project = project_update;
      res.company = company;
      yield put(actionCreator.callbackCreateProjectDetailAction(true, res));
      // yield put(actionCreatorParent.requestGetAllByClientProjectListAction());
      yield put(
        change("clientProjectMainCreateForm", "thumbnailURL", thumbnailURL)
      );
      yield put(stopSubmit("clientProjectMainCreateForm"));
      yield put(setSubmitSucceeded("clientProjectMainCreateForm"));
      yield put(push(`/client/projects`));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationClient.successCreateProject;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackCreateProjectDetailAction(false));
      yield put(
        stopSubmit("clientProjectMainCreateForm", { _error: error.toString() })
      );
    }
  }
}

function* handleUpdateProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestUpdateProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_UPDATE_PROJECT
      );
      yield put(startSubmit("clientProjectMainModifyForm"));

      const status = action.item.status;
      const projectId = action.item.id;
      switch (Number(status)) {
        case Status.Status_Open:
          yield call(openProject, projectId);
          break;
        case Status.Status_Draft:
          yield call(draftProject, projectId);
          break;
        case Status.Status_Close:
          yield call(closeProject, projectId);
          break;
        case Status.Status_OnlyScout:
          yield call(onlyScoutProject, projectId);
          break;
      }

      let thumbnailURL: string;
      const authSelector = (state: AppState) => state.authReducer;
      const _authState: AuthState = yield select(authSelector);
      const user: User | null = _authState.user;
      if (action.item.uploadFileThumbnailURL) {
        thumbnailURL = yield call(
          createProjectThumbnail,
          user,
          action.item.uploadFileThumbnailURL,
          action.item.id
        );
      } else if (/^https:/.test(action.item.thumbnailURL)) {
        // httpsで始まる場合はAPIが自動生成するデフォルト画像
        thumbnailURL = "";
      } else if (/^gs:/.test(action.item.thumbnailURL)) {
        // GS形式で始まる場合はすでにAPIへ登録済 && 更新が存在しない場合
        thumbnailURL = action.item.thumbnailURL;
      } else {
        // 仕様外動作として初期値を入れている
        thumbnailURL = "";
      }

      const updateParams: TUpdateProjectParams = {
        id: action.item.id,
        name: action.item.name,
        description: action.item.description,
        background: action.item.background,
        preferentialExperience: action.item.preferentialExperience,
        thumbnailURL,
        place: action.item.place,
        workingHourRange: action.item.workingHourRange,
        paymentRange: action.item.paymentRange,
        workingDuration: action.item.workingDuration,
        occupations: action.item.occupations,
        operatingDate: action.item.operatingDate,
        isHideCompany: action.item.isHideCompany,
      };

      const project: Project = yield call(updateProject, updateParams);
      const res: UpdateProjectDetailByClientResponse =
        new UpdateProjectDetailByClientResponse();
      res.project = project;
      yield put(actionCreator.callbackUpdateProjectDetailAction(true, res));
      yield put(
        change("clientProjectMainModifyForm", "thumbnailURL", thumbnailURL)
      );
      yield put(stopSubmit("clientProjectMainModifyForm"));
      yield put(setSubmitSucceeded("clientProjectMainModifyForm"));
      yield put(push(`/client/projects`));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationClient.successUpdateProject;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackUpdateProjectDetailAction(false));
      yield put(
        stopSubmit("clientProjectMainModifyForm", { _error: error.toString() })
      );
    }
  }
}

function* handleDraftProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestDraftProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_DRAFT_PROJECT
      );
      yield put(startSubmit("clientProjectStateForm"));
      yield call(draftProject, action.item.projectId);
      yield put(actionCreator.callbackDraftProjectDetailAction(true));
      yield put(stopSubmit("clientProjectStateForm"));
      yield put(setSubmitSucceeded("clientProjectStateForm"));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackDraftProjectDetailAction(false));
      yield put(
        stopSubmit("clientProjectStateForm", { _error: error.toString() })
      );
    }
  }
}

function* handleOpenProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestOpenProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_OPEN_PROJECT
      );
      yield put(startSubmit("clientProjectStateForm"));
      yield call(openProject, action.item.projectId);
      yield put(actionCreator.callbackOpenProjectDetailAction(true));
      yield put(stopSubmit("clientProjectStateForm"));
      yield put(setSubmitSucceeded("clientProjectStateForm"));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackOpenProjectDetailAction(false));
      yield put(
        stopSubmit("clientProjectStateForm", { _error: error.toString() })
      );
    }
  }
}

function* handleCloseProjectDetailInClientProjectDetail() {
  while (true) {
    try {
      const action: IRequestCloseProjectDetailAction = yield take(
        ClientProjectDetailActionType.REQUEST_CLOSE_PROJECT
      );
      yield put(startSubmit("clientProjectStateForm"));
      yield call(closeProject, action.item.projectId);
      yield put(actionCreator.callbackCloseProjectDetailAction(true));
      yield put(stopSubmit("clientProjectStateForm"));
      yield put(setSubmitSucceeded("clientProjectStateForm"));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackCloseProjectDetailAction(false));
      yield put(
        stopSubmit("clientProjectStateForm", { _error: error.toString() })
      );
    }
  }
}

const getProject = (projectID: string): Promise<Project> => {
  return projectRepository.get(projectID);
};

const getCompany = (companyId: string): Promise<Company> => {
  return companyRepository.get(companyId);
};

const createProject = (
  createProjectParams: TCreateProjectParams
): Promise<Project> => {
  return projectRepository.create(createProjectParams);
};

const updateProject = (
  updateProjectParams: TUpdateProjectParams
): Promise<Project> => {
  return projectRepository.update(updateProjectParams);
};

const draftProject = (projectID: string): Promise<void> => {
  return projectRepository.draft(projectID);
};

const openProject = (projectID: string): Promise<void> => {
  return projectRepository.open(projectID);
};

const closeProject = (projectID: string): Promise<void> => {
  return projectRepository.close(projectID);
};

const onlyScoutProject = (projectID: string): Promise<void> => {
  return projectRepository.onlyScout(projectID);
};

const createProjectThumbnail = (
  user: User,
  uploadFile: File,
  projectId: string
): Promise<string> => {
  return fileUseCase.createProjectThumbnail(user, uploadFile, projectId);
};

export {
  handleCloseProjectDetailInClientProjectDetail,
  handleCreateProjectDetailInClientProjectDetail,
  handleDraftProjectDetailInClientProjectDetail,
  handleGetProjectDetailInClientProjectDetail,
  handleOpenProjectDetailInClientProjectDetail,
  handleUpdateProjectDetailInClientProjectDetail,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_project_handler.ts